import { useCallback } from "react";
import { useAppStore } from "@/store";
import { EventContext } from "@/domains/metrics/context";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { useEventListener } from "usehooks-ts";

/**
 * Paste handler that creates a new note when pasting outside editable fields.
 *
 * This hook listens to document-level paste events and:
 * 1. Checks if the paste originated from an editable field (input, textarea, contentEditable)
 * 2. If yes, allows native paste behavior to proceed
 * 3. If no, prevents default and creates a new note with the pasted content
 *
 * The paste event bubbles up the DOM, so we check event.target (where paste originated)
 * rather than document.activeElement.
 *
 * @param options.isEnabled - Whether paste handling is enabled (controlled by feature flag)
 */
export const usePasteHandler = () => {
  const { store } = useAppStore();
  const handlePaste = useCallback(
    (event: ClipboardEvent) => {
      const target = event.target as Element;

      // common editor neatly does this
      if (event.defaultPrevented) {
        return;
      }

      // If paste originated from an editable field, let native paste proceed
      if (isEditableField(target)) {
        logger.debug({
          message: "[usePasteHandler] Paste in editable field, allowing native behavior",
          info: {
            tagName: target.tagName,
            className: target.className,
            isContentEditable: (target as HTMLElement).isContentEditable,
          },
        });
        return;
      }

      // Check if a note is currently being created
      if (store.notes.newNoteIsBeingCreated) {
        return;
      }

      // Get clipboard data
      const clipboardData = event.clipboardData;
      if (!clipboardData) {
        logger.debug({
          message: "[usePasteHandler] No clipboard data available",
        });
        return;
      }

      // Check if clipboard has any content
      const hasText = clipboardData.types.includes("text/plain");
      const hasHtml = clipboardData.types.includes("text/html");
      const hasFiles = clipboardData.files.length > 0;

      if (!hasText && !hasHtml) {
        logger.debug({
          message: "[usePasteHandler] Clipboard is empty",
        });
        return;
      }

      if (hasFiles) {
        // we allow Drag and Drop to haddle files
        logger.debug({
          message: "[usePasteHandler] Clipboard has files",
        });

        return;
      }

      // Prevent default paste behavior
      event.preventDefault();
      event.stopPropagation();

      try {
        logger.debug({
          message: "[usePasteHandler] Creating note from paste",
          info: {
            hasText,
            hasHtml,
            hasFiles,
            fileCount: clipboardData.files.length,
          },
        });

        store.notes.composeNewNote({
          eventContext: EventContext.NotesActions,
          clipboardEvent: event,
        });
      } catch (error) {
        logger.error({
          message: "[usePasteHandler] Error creating note from paste",
          info: { error: objectModule.safeErrorAsJson(error as Error) },
        });
      }
    },
    [store]
  );

  useEventListener("paste", handlePaste);
};

/**
 * Determines if an element is an editable field where native paste should be allowed.
 * This includes text inputs, textareas, and contentEditable elements
 *
 * @param element - The element to check
 * @returns true if element is editable and should handle paste natively
 */
const isEditableField = (element: Element | null): boolean => {
  if (!element) return false;

  const forbiddenTags = ["INPUT", "TEXTAREA"];

  if (forbiddenTags.includes(element.tagName)) {
    return true;
  }

  // Check if the element or any of its parents are contentEditable
  const htmlElement = element as HTMLElement;
  if (htmlElement.isContentEditable) {
    return true;
  }

  return false;
};
